import type { ConfigurationGeneratorBuilderContextI } from '@ingenico/react-configuration-generator';
import { checkFieldIsSmartFieldByInputKey } from '@ingenico/react-configuration-generator';
import type { Settings } from '@ingenico/react-form-builder';
import {
  FGGridHintRenderPositionEnum,
  HintClickBehaviorEnum,
  SmartfieldDisplayModeEnum,
  SmartfieldStrategyEnum,
} from '@ingenico/react-form-builder';
import { useCurrentOrganizationNameId, useCurrentUser, useSectionContainer } from '@ingenico/react-section-utils';
import { useStore } from 'jotai';
import { toString } from 'lodash-es';
import { useCallback, useMemo, useRef } from 'react';
import { shareReplay, switchMap, take } from 'rxjs';
import { selectedLogicalDeviceIdAtom } from '../../../atoms/selectedLogicalDevice.atom';
import { ConfigurationFormGeneratorService } from '../../../domain/business/services/configurationFormGenerator.service';
import type { TemplateDetailedResource } from '../../../domain/models/templateResource.model';
import { useDeviceManagementCapability } from '../../../hooks';
import { useOpenSmartFieldDetailPage } from '../../../hooks/useOpenSmartFieldDetailsPage.hook';
import styles from '../ExposedConfigurationFormBuilderWrapper.module.scss';

export interface UseExposedConfigurationFormBuilderWrapperProviderValueV2Return
  extends Omit<ConfigurationGeneratorBuilderContextI, 'getFormBuilderConfig'> {
  readonly getFormBuilderConfig: (
    templateId: string,
    isSnapshotId: boolean,
    configurationModuleId: string,
    deploymentUnitId: string,
    smartFieldPrefix: string,
    options?: { readonly isRefresh: boolean },
  ) => ReturnType<ConfigurationGeneratorBuilderContextI['getFormBuilderConfig']>;
}

export function useExposedConfigurationFormBuilderWrapperProviderValueV2(): UseExposedConfigurationFormBuilderWrapperProviderValueV2Return {
  const currentUser = useCurrentUser();
  const shouldDisplayDeploymentsTab = useDeviceManagementCapability('configProfile.useDeploymentConfigurationFlow');
  const shouldSendDefaultValue = !useDeviceManagementCapability('configProfile.skipDefaultTemplateValues');

  const store = useStore();
  const container = useSectionContainer();

  const _userDomain = useCurrentOrganizationNameId();

  const configurationFormGeneratorService = useMemo(
    () => container.get(ConfigurationFormGeneratorService),
    [container],
  );

  const isDmMultiMerchantEnabled = useDeviceManagementCapability('deployments.isDmMultiMerchantEnabled');
  const prevOutputIfFFIsTrue = useRef<ReturnType<
    ConfigurationFormGeneratorService['getFormBuilderConfigBuilder']
  > | null>(null);

  const getFormBuilderConfig = useCallback(
    (
      templateId: string,
      isSnapshotId: boolean,
      configurationModuleId: string,
      deploymentUnitId: string,
      smartFieldPrefix: string,
      options?: { readonly isRefresh: boolean },
    ) => {
      const logicalDeviceId = store.get(selectedLogicalDeviceIdAtom);
      const outputIfFFIsFalse = configurationFormGeneratorService.getFormBuilderConfigBuilder(
        templateId,
        currentUser.locale,
        configurationModuleId,
        isSnapshotId,
        deploymentUnitId,
        smartFieldPrefix,
      );

      const previousOutput = prevOutputIfFFIsTrue.current;

      // On refresh, reuse the template from the previous output instead of requesting it again
      const rawOutput =
        options?.isRefresh && previousOutput
          ? previousOutput.pipe(
              take(1),
              switchMap(({ templateToDisplay }) =>
                configurationFormGeneratorService.getFormBuilderConfigForVirtualDeviceBuilderFromExistingTemplate({
                  template: templateToDisplay as unknown as TemplateDetailedResource,
                  configurationModuleId,
                  isSnapshotId,
                  deploymentUnitId,
                  logicalDeviceId,
                  isDmMultiMerchantEnabled,
                }),
              ),
            )
          : configurationFormGeneratorService.getFormBuilderConfigForVirtualDeviceBuilder({
              templateId,
              configurationModuleId,
              isSnapshotId,
              deploymentUnitId,
              logicalDeviceId,
              isDmMultiMerchantEnabled,
            });

      // Cached so that reading the template later does not trigger the requests again
      const configurationOutput = rawOutput.pipe(shareReplay({ bufferSize: 1, refCount: false }));

      prevOutputIfFFIsTrue.current = configurationOutput;

      return shouldDisplayDeploymentsTab ? configurationOutput : outputIfFFIsFalse;
    },
    [
      configurationFormGeneratorService,
      currentUser.locale,
      shouldDisplayDeploymentsTab,
      store,
      isDmMultiMerchantEnabled,
    ],
  );

  const getFieldClassName = useCallback(
    (smartFieldsId: string[], inputKey?: string) =>
      checkFieldIsSmartFieldByInputKey(smartFieldsId, toString(inputKey)) && !shouldDisplayDeploymentsTab
        ? styles.smartFieldInput
        : '',
    [shouldDisplayDeploymentsTab],
  );
  const { openSmartFieldDetailPage } = useOpenSmartFieldDetailPage();

  const settings = useMemo<Settings>(() => {
    return shouldDisplayDeploymentsTab
      ? {
          componentToggler: { header: false, editButton: false, inputs: { topHint: true, gear: false } },
          smartFieldMappingStrategy: SmartfieldStrategyEnum.BySmartfieldKey,
          smartfieldDisplayMode: SmartfieldDisplayModeEnum.ShowValue,

          FGGridSettings: {
            hintRenderPosition: FGGridHintRenderPositionEnum.Top,
            hintClickBehavior: HintClickBehaviorEnum.RedirectToSmartfieldDetails,
            isAlwaysEnableRowClick: true,
          },
          isReadOnly: true,
          shouldSendDefaultValue,
        }
      : {
          componentToggler: { header: false, editButton: false, inputs: { topHint: false, gear: false } },
          smartFieldMappingStrategy: SmartfieldStrategyEnum.ByTypeValue,
          smartfieldDisplayMode: SmartfieldDisplayModeEnum.ShowName,
          FGGridSettings: {
            hintRenderPosition: FGGridHintRenderPositionEnum.Bottom,
            hintClickBehavior: HintClickBehaviorEnum.Unclickable,
            isAlwaysEnableRowClick: true,
          },
          isReadOnly: true,
          shouldSendDefaultValue,
        };
  }, [shouldDisplayDeploymentsTab, shouldSendDefaultValue]);

  return {
    getFieldClassName,
    getFormBuilderConfig,
    openSmartFieldDetailPage,
    settings,
  };
}
